import React, { useState, useEffect, useMemo } from 'react';
import { Modal, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import {
  Calendar,
  FileText,
  Calculator,
  Paperclip,
  Eye,
  Edit3,
  Copy,
  Trash2,
  Upload,
  AlertTriangle,
  CheckCircle,
  Image,
  File,
  User,
  Plus,
  Search
} from 'lucide-react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import DeleteVerificationModal from './DeleteVerificationModal';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import swal from 'sweetalert';

const getVerificationPermissions = (verificationData) => {
  const permissions = {
    canDelete: true,
    canEdit: true,
    reason: null
  };

  if (!verificationData || !verificationData.references) {
    return permissions;
  }

  // Check if this is an annulment verification
  if (verificationData.isAnnulmentVerification) {
    permissions.canDelete = false;
    permissions.canEdit = false;
    permissions.reason = 'Detta är ett annuleringsverifikat och kan inte ändras.';
    return permissions;
  }

  // Check if this verification has been annulled
  if (verificationData.hasBeenAnnulled) {
    permissions.canDelete = false;
    permissions.canEdit = false;
    permissions.reason = 'Detta verifikat har redan blivit annulerat.';
    return permissions;
  }

  // Check source references (what this verification refers to)
  for (const ref of verificationData.references?.asSource || []) {
    switch (ref.reference_type) {
      case 'customer_invoice_payment':
        permissions.canDelete = false;
        permissions.canEdit = false;
        permissions.reason = 'Detta verifikat är kopplat till en fakturabetalning och kan inte ändras här. Gå till fakturavyn för att hantera betalningen.';
        return permissions;

      case 'payment_reversal':
        permissions.canDelete = false;
        permissions.canEdit = false;
        permissions.reason = 'Detta verifikat är kopplat till en betalningsannulering och kan inte ändras.';
        return permissions;
    }
  }

  // Check target references (what refers to this verification)
  for (const ref of verificationData.references?.asTarget || []) {
    if (ref.reference_type === 'verification_reversal') {
      permissions.canDelete = false;
      permissions.canEdit = false;
      permissions.reason = 'Detta verifikat har redan annullerats.';
      return permissions;
    }
  }

  // Check if this is an invoice related verification
  if (verificationData.invoice) {
    permissions.canDelete = false;
    permissions.canEdit = false;
    permissions.reason = 'Detta verifikat är kopplat till en faktura och kan inte ändras här. Gå till fakturavyn för att hantera det.';
    return permissions;
  }

  return permissions;
};

const InvoiceSection = ({ invoice, companyId }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl;

    const fetchPdf = async () => {
      try {
        const response = await axios.get(
          getApiUrl(`${companyId}/invoices/${invoice.invoice_id}/pdf`),
          {
            responseType: 'blob',
            withCredentials: true,
          }
        );

        objectUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        setPdfUrl(objectUrl);
      } catch (error) {
        console.error('Error fetching PDF:', error);
        setError('Det gick inte att ladda fakturan. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    if (invoice?.invoice_id) {
      fetchPdf();
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [invoice?.invoice_id, companyId]);

  if (!invoice?.invoice_id) return null;

  return (
    <div className="verification-section">
      <div className="d-flex align-items-center mb-3">
        <FileText className="section-icon me-2" />
        <h6 className="mb-0 fw-medium">Faktura #{invoice.invoice_number}</h6>
      </div>
      {loading ? (
        <div className="text-center py-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Laddar...</span>
          </div>
          <p className="mt-2 small">Laddar faktura...</p>
        </div>
      ) : error ? (
        <div className="text-center py-3">
          <div className="alert alert-warning">
            <p className="mb-3 small">{error}</p>
            <button
              className="action-button"
              onClick={() => window.open(`/${companyId}/view-invoice/${invoice.invoice_id}`, '_blank')}
            >
              <FileText size={14} />
              Öppna faktura i ny flik
            </button>
          </div>
        </div>
      ) : (
        <div className="border rounded overflow-hidden">
          <object
            data={pdfUrl}
            type="application/pdf"
            style={{
              width: '100%',
              height: '300px',
              border: 'none'
            }}
          >
            <div className="text-center py-3">
              <FileText size={32} className="text-muted mb-2" />
              <p className="small">PDF kunde inte visas</p>
            </div>
          </object>
        </div>
      )}
    </div>
  );
};

const VerificationDetailsModal = ({
  showVerificationDetails,
  setShowVerificationDetails,
  selectedRow,
  onVerificationUpdated,
  onUploadAttachment,
}) => {
  const { companyId } = useParams();
  const location = useLocation();
  const history = useHistory();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [hoveredAttachment, setHoveredAttachment] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [textContent, setTextContent] = useState(null);

  console.log('Selected Row:', selectedRow);

  // Calculate permissions based on verification references
  const permissions = useMemo(() => {
    return getVerificationPermissions(selectedRow);
  }, [selectedRow]);

  // Reset state when modal opens or closes
  useEffect(() => {
    if (!showVerificationDetails) {
      setAttachments([]);
      setAttachmentError(null);
      setLoadingAttachments(false);
      setLoadingPreview(false); // Clear preview loading state
      
      // Clean up preview blob URL and text content
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl(null);
      }
      setTextContent(null);
      setHoveredAttachment(null);
    }
  }, [showVerificationDetails, previewBlobUrl]);

  // Fetch attachments when verification is selected
  useEffect(() => {
    if (selectedRow?.verification_id && showVerificationDetails) {
      fetchAttachments();
    }
  }, [selectedRow, showVerificationDetails]);

  // Fetch attachments
  const fetchAttachments = async () => {
    if (!selectedRow?.verification_id) return;

    setLoadingAttachments(true);
    setAttachmentError(null);

    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/accounting/verifications/${selectedRow.verification_id}/attachments`),
        { withCredentials: true }
      );
      const attachmentsData = response.data.attachments || [];

      const attachmentsWithThumbnails = await Promise.all(
        attachmentsData.map(async (attachment) => {
          try {
            const thumbnailResponse = await axios.get(
              getApiUrl(`${companyId}/accounting/verifications/attachments/${attachment.attachment_id}/thumbnail`),
              {
                responseType: 'blob',
                withCredentials: true,
              }
            );
            const thumbnailUrl = URL.createObjectURL(thumbnailResponse.data);
            return { ...attachment, thumbnailUrl };
          } catch (error) {
            console.error('Error fetching attachment thumbnail:', error);
            return { ...attachment, thumbnailUrl: null, error: true };
          }
        })
      );

      console.log('Fetched attachments with thumbnails:', attachmentsWithThumbnails);

      setAttachments(attachmentsWithThumbnails);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setAttachmentError('Det gick inte att hämta bilagorna. Försök igen senare.');
    } finally {
      setLoadingAttachments(false);
    }
  };

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      attachments.forEach((attachment) => {
        if (attachment.thumbnailUrl) {
          URL.revokeObjectURL(attachment.thumbnailUrl);
        }
      });
    };
  }, [attachments]);

  // File upload handler
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);

    try {
      for (const file of files) {
        // Validate file size (10MB limit)
        if (file.size > 10 * 1024 * 1024) {
          swal("Fel", `Filen "${file.name}" är för stor. Maximal filstorlek är 10MB.`, "error");
          continue;
        }

        // Validate file type
        const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xlsx', '.xls', '.txt'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(fileExtension)) {
          swal("Fel", `Filtypen för "${file.name}" stöds inte. Tillåtna filtyper: ${allowedTypes.join(', ')}`, "error");
          continue;
        }

        // Create form data for upload
        const formData = new FormData();
        formData.append('file', file);

        try {
          const uploadResponse = await axios.post(
            getApiUrl(`${companyId}/accounting/verifications/${selectedRow.verification_id}/attachments`),
            formData,
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );

          if (uploadResponse.data.success) {
            // Refresh attachments after successful upload
            await fetchAttachments();
            //swal("Framgång", `Filen "${file.name}" har laddats upp.`, "success");
          } else {
            throw new Error(uploadResponse.data.message || "Upload failed");
          }
        } catch (uploadError) {
          swal("Fel", `Det gick inte att ladda upp "${file.name}". Försök igen.`, "error");
        }
      }
    } catch (error) {
      swal("Fel", 'Det gick inte att ladda upp filerna. Försök igen senare.', "error");
    } finally {
      setUploadingFiles(false);
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleDelete = () => {
    if (!permissions.canDelete) {
      swal("Kan inte radera", permissions.reason, "warning");
      return;
    }
    setShowDeleteModal(true);
    setShowVerificationDetails(false);
  };

  const handleEdit = () => {
    if (!permissions.canEdit) {
      swal("Kan inte redigera", permissions.reason, "warning");
      return;
    }
    setShowVerificationDetails(false);
    openEditVerificationModal(selectedRow);
  };

  const handleCopy = () => {
    setShowVerificationDetails(false);
    openCreateVerificationModal(selectedRow);
  };

  const openCreateVerificationModal = (verificationData) => {
    history.push({
      pathname: `/${companyId}/copy-verification`,
      state: { verificationData, from: location.pathname + '?updated=true' },
    });
  };

  const openEditVerificationModal = (verificationData) => {
    history.push({
      pathname: `/${companyId}/edit-verification/${verificationData.verification_id}`,
      state: { verificationData, from: location.pathname + '?updated=true' },
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  const handleCloseModal = () => {
    setShowVerificationDetails(false);
    if (onVerificationUpdated) {
      onVerificationUpdated();
    }
  };

  // Function to delete an attachment
  const deleteAttachment = async (attachmentId, fileName) => {
    try {
      const confirmDelete = await swal({
        title: "Ta bort bilaga?",
        text: `Är du säker på att du vill ta bort "${fileName}"? Detta kan inte ångras.`,
        icon: "warning",
        buttons: {
          cancel: {
            text: "Avbryt",
            value: false,
            visible: true,
            className: "btn-secondary"
          },
          confirm: {
            text: "Ta bort",
            value: true,
            visible: true,
            className: "btn-danger"
          }
        },
        dangerMode: true,
      });

      if (!confirmDelete) return;

      const response = await axios.delete(
        getApiUrl(`${companyId}/accounting/verifications/attachments/${attachmentId}`),
        { withCredentials: true }
      );

      if (response.data.success) {
        // Remove the attachment from the local state
        setAttachments(prevAttachments => 
          prevAttachments.filter(att => att.attachment_id !== attachmentId)
        );

        // Clear any active preview if it was for this attachment
        if (hoveredAttachment?.attachment_id === attachmentId) {
          handleAttachmentLeave();
        }

        // Refresh attachments after successful deletion
        if (onVerificationUpdated) {
          onVerificationUpdated();
        }
      } else {
        swal("Fel", response.data.message || "Kunde inte ta bort bilagan", "error");
      }
    } catch (error) {
      swal("Fel", "Ett fel uppstod när bilagan skulle tas bort", "error");
    }
  };

  // Enhanced fetchAttachmentBlob function (keeping existing code)
  const canPreviewFile = (fileName, fileType) => {
    if (!fileName) return false;
    
    const extension = fileName.toLowerCase().split('.').pop();
    
    // Check by file type first (if not generic)
    if (fileType && fileType !== 'application/octet-stream') {
      return fileType.startsWith('image/') || 
             fileType === 'application/pdf' || 
             fileType.startsWith('text/');
    }
    
    // If file type is generic or unknown, check by extension
    const previewableExtensions = ['jpg', 'jpeg', 'png', 'gif', 'pdf', 'txt', 'csv', 'json', 'xml'];
    return previewableExtensions.includes(extension);
  };

  // Function to fetch attachment data and create blob URL
  const fetchAttachmentBlob = async (attachment) => {
    if (!attachment) return null;
    
    // Check if file can be previewed before attempting fetch
    if (!canPreviewFile(attachment.file_name, attachment.file_type)) {
      console.log('File cannot be previewed based on type/extension:', attachment.file_name, attachment.file_type);
      return null;
    }
    
    try {
      setLoadingPreview(true);
      console.log('Fetching attachment blob for:', attachment.file_name);
      console.log('File type:', attachment.file_type);
      console.log('API URL:', getApiUrl(`${companyId}/attachments/${attachment.attachment_id}`));
      
      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachment_id}`),
        {
          responseType: 'blob',
          withCredentials: true,
          // Add timeout to prevent hanging requests
          timeout: 10000,
        }
      );
      
      console.log('Response received:', response);
      console.log('Response headers:', response.headers);
      console.log('Blob size:', response.data.size);
      console.log('Blob type:', response.data.type);
      
      // Validate that we actually got a blob
      if (response.data && response.data.size > 0) {
        const blobUrl = URL.createObjectURL(response.data);
        console.log('Created blob URL:', blobUrl);
        return blobUrl;
      } else {
        console.warn('Received empty blob for attachment:', attachment.file_name);
        return null;
      }
    } catch (error) {
      console.error('Error fetching attachment blob:', error);
      // Don't re-throw the error to prevent downloads
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  // Function to fetch text content for text files
  const fetchTextContent = async (attachment) => {
    if (!attachment) return null;
    
    // Check if this is a text file by type or extension
    const isTextFile = attachment.file_type?.startsWith('text/') || 
                      ['txt', 'csv', 'json', 'xml'].includes(
                        attachment.file_name?.toLowerCase().split('.').pop()
                      );
    
    if (!isTextFile) return null;
    
    try {
      setLoadingPreview(true);
      console.log('Fetching text content for:', attachment.file_name);
      
      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachment_id}`),
        {
          responseType: 'text',
          withCredentials: true,
          // Add timeout to prevent hanging requests
          timeout: 10000,
        }
      );
      
      console.log('Text content fetched, length:', response.data?.length);
      return response.data;
    } catch (error) {
      console.error('Error fetching text content:', error);
      // Don't re-throw the error to prevent downloads
      return null;
    } finally {
      setLoadingPreview(false);
    }
  };

  // Hover handlers for attachment preview
  const handleAttachmentHover = async (attachment, event) => {
    console.log('Hovering attachment:', attachment);
    
    const rect = event.currentTarget.getBoundingClientRect();
    setPreviewPosition({
      x: rect.left + rect.width + 10,
      y: rect.top
    });
    setHoveredAttachment(attachment);
    
    // Clean up previous blob URL and text content
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setTextContent(null);
    setLoadingPreview(false); // Reset loading state
    
    try {
      // Fetch appropriate preview content based on file type
      if (attachment.file_type.startsWith('image/') || attachment.file_type === 'application/pdf') {
        const blobUrl = await fetchAttachmentBlob(attachment);
        if (blobUrl) {
          setPreviewBlobUrl(blobUrl);
        } else if (attachment.thumbnailUrl && attachment.file_type.startsWith('image/')) {
          // Fallback to thumbnail only for images
          setPreviewBlobUrl(attachment.thumbnailUrl);
        }
      } else if (attachment.file_type.startsWith('text/')) {
        const content = await fetchTextContent(attachment);
        if (content) {
          setTextContent(content);
        }
      }
    } catch (error) {
      console.error('Error in attachment hover handler:', error);
      // Ensure loading state is cleared even if something goes wrong
      setLoadingPreview(false);
    }
  };

  const handleAttachmentLeave = () => {
    setHoveredAttachment(null);
    setLoadingPreview(false); // Ensure loading state is cleared
    
    // Clean up blob URL and text content
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setTextContent(null);
  };

  // Click handler for download
  const handleAttachmentClick = async (attachment) => {
    try {
      console.log('Downloading attachment:', attachment.file_name);
      
      const response = await axios.get(
        getApiUrl(`${companyId}/attachments/${attachment.attachment_id}`),
        {
          responseType: 'blob',
          withCredentials: true,
        }
      );
      
      // Create blob URL and download
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      swal("Fel", `Det gick inte att ladda ner filen "${attachment.file_name}". Försök igen.`, "error");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format creator information
  const formatCreatorInfo = (createdByInfo) => {
    if (!createdByInfo) return 'Okänd användare';
    
    if (createdByInfo.full_name && createdByInfo.full_name.trim()) {
      return createdByInfo.full_name.trim();
    }
    
    if (createdByInfo.first_name || createdByInfo.last_name) {
      return `${createdByInfo.first_name || ''} ${createdByInfo.last_name || ''}`.trim();
    }
    
    if (createdByInfo.email) {
      return createdByInfo.email;
    }
    
    return 'Okänd användare';
  };

  // Calculate if verification is balanced
  const isBalanced = Math.abs((selectedRow?.debitSum || 0) - (selectedRow?.creditSum || 0)) <= 0.01;

  return (
    <>
      <Modal
        show={showVerificationDetails}
        onHide={handleCloseModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="border-0 pb-2">
          <Modal.Title className="h5 fw-medium">
            <div className="d-flex align-items-center">
              <Eye className="me-2" size={20} style={{ color: '#3b82f6' }} />
              <div>
                <div className="fw-medium">
                  Verifikat {selectedRow?.series}{selectedRow?.number}
                  {(!permissions.canEdit || !permissions.canDelete) && (
                    <OverlayTrigger
                      placement="right"
                      overlay={<Tooltip>{permissions.reason}</Tooltip>}
                    >
                      <FontAwesomeIcon
                        icon={faLock}
                        className="ms-2 text-muted"
                        size="sm"
                      />
                    </OverlayTrigger>
                  )}
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 py-3">
          <style>{`
            .verification-section {
              background: #f8fafc;
              border-radius: 8px;
              padding: 1rem;
              margin-bottom: 1rem;
              border: 1px solid #e2e8f0;
            }
            
            .section-icon {
              width: 16px;
              height: 16px;
              color: #64748b;
            }
            
            .balance-indicator {
              display: inline-flex;
              align-items: center;
              gap: 0.25rem;
              padding: 0.25rem 0.75rem;
              border-radius: 6px;
              font-weight: 500;
              font-size: 0.75rem;
            }
            
            .balance-good {
              background: #dcfce7;
              color: #166534;
              border: 1px solid #bbf7d0;
            }
            
            .balance-bad {
              background: #fef2f2;
              color: #dc2626;
              border: 1px solid #fecaca;
            }
            
            .compact-table {
              background: white;
              border-radius: 6px;
              overflow: hidden;
              border: 1px solid #e2e8f0;
            }
            
            .compact-table-header {
              background: #f8fafc;
              border-bottom: 1px solid #e2e8f0;
              padding: 0.5rem 0.75rem;
              font-size: 0.75rem;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.025em;
              color: #64748b;
            }
            
            .compact-table-row {
              padding: 0.5rem 0.75rem;
              border-bottom: 1px solid #f1f5f9;
              font-size: 0.875rem;
              transition: background-color 0.15s ease;
            }
            
            .compact-table-row:hover {
              background: #f8fafc;
            }
            
            .compact-table-row:last-child {
              border-bottom: none;
            }
            
            .compact-table-footer {
              background: #f1f5f9;
              padding: 0.5rem 0.75rem;
              font-weight: 600;
              font-size: 0.875rem;
              border-top: 1px solid #e2e8f0;
            }
            
            .amount-cell {
              font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
              font-size: 0.8rem;
            }
            
            .attachment-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
              gap: 0.75rem;
            }
            
            .attachment-card {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 0.75rem;
              transition: all 0.2s ease;
            }
            
            .attachment-card:hover {
              border-color: #3b82f6;
              box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
              transform: translateY(-1px);
            }
            
            /* New File Item Styles */
            .file-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 80px;
              padding: 8px;
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              transition: all 0.2s ease;
              cursor: pointer;
              position: relative;
            }
            
            .file-item:hover {
              border-color: #44814E;
              background: #f8fafc;
              transform: translateY(-1px);
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            
            .file-upload-item {
              border: 2px dashed #cbd5e1;
              background: #f8fafc;
            }
            
            .file-upload-item:hover {
              border-color: #44814E;
              background: white;
            }
            
            .file-upload-label {
              display: flex;
              flex-direction: column;
              align-items: center;
              width: 100%;
              cursor: pointer;
              text-decoration: none;
              color: inherit;
            }
            
            .file-icon-container {
              position: relative;
              margin-bottom: 4px;
            }
            
            .file-icon {
              color: #64748b;
            }
            
            .file-thumbnail {
              width: 24px;
              height: 24px;
              object-fit: cover;
              border-radius: 4px;
            }
            
            .upload-icon {
              color: #44814E;
            }
            
            .upload-spinner {
              color: #44814E;
            }
            
            .file-info {
              text-align: center;
              width: 100%;
            }
            
            .file-name {
              font-size: 10px;
              font-weight: 500;
              color: #374151;
              line-height: 1.2;
              margin-bottom: 2px;
              word-break: break-word;
            }
            
            .file-size {
              font-size: 9px;
              color: #6b7280;
              line-height: 1;
            }
            
            .file-upload-item .file-name {
              color: #44814E;
            }
            
            /* Hover Preview Styles */
            .attachment-hover-preview {
              position: fixed;
              z-index: 10000;
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
              padding: 16px;
              max-width: 450px;
              max-height: 500px;
              pointer-events: none;
              transform: translateX(0) translateY(-50%);
            }
            
            .preview-header {
              font-size: 12px;
              font-weight: 600;
              color: #374151;
              margin-bottom: 8px;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            
            .preview-content {
              position: relative;
              overflow: hidden;
              border-radius: 6px;
              background: #f8fafc;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 180px;
            }
            
            .preview-image {
              max-width: 100%;
              max-height: 400px;
              object-fit: contain;
              border-radius: 4px;
            }
            
            .preview-pdf-container {
              width: 100%;
              height: 320px;
              border: 1px solid #e2e8f0;
              border-radius: 4px;
              background: white;
            }
            
            .preview-pdf-object {
              width: 100%;
              height: 100%;
              border: none;
              border-radius: 4px;
            }
            
            .preview-pdf-iframe {
              width: 100%;
              height: 100%;
              border: none;
              border-radius: 4px;
            }
            
            .preview-text-container {
              width: 100%;
              max-height: 300px;
              overflow-y: auto;
              border: 1px solid #e2e8f0;
              border-radius: 4px;
              background: white;
            }
            
            .preview-text-content {
              padding: 12px;
              margin: 0;
              font-size: 12px;
              line-height: 1.5;
              font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
              white-space: pre-wrap;
              word-break: break-word;
            }
            
            .preview-placeholder {
              text-align: center;
              padding: 40px 20px;
              color: #64748b;
              font-size: 14px;
            }
            
            .preview-info {
              margin-top: 8px;
              font-size: 11px;
              color: #6b7280;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .magnifying-icon {
              color: #3b82f6;
              animation: pulse 2s infinite;
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.7; }
            }
            
            .info-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 1rem;
            }
            
            .info-card {
              background: white;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 0.75rem;
            }
            
            .info-label {
              font-size: 0.7rem;
              color: #64748b;
              text-transform: uppercase;
              letter-spacing: 0.025em;
              margin-bottom: 0.25rem;
              font-weight: 600;
            }
            
            .info-value {
              font-size: 0.875rem;
              font-weight: 500;
              color: #1e293b;
            }
            
            .creator-info-card {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 0.75rem;
              display: flex;
              align-items: center;
              gap: 0.5rem;
            }
            
            .creator-avatar {
              width: 32px;
              height: 32px;
              border-radius: 50%;
              background: linear-gradient(135deg, #3b82f6, #1d4ed8);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: 600;
              font-size: 0.75rem;
            }
            
            .creator-details {
              flex: 1;
            }
            
            .creator-name {
              font-weight: 600;
              font-size: 0.875rem;
              color: #1e293b;
              margin-bottom: 0.125rem;
            }
            
            .creator-email {
              font-size: 0.75rem;
              color: #64748b;
            }
            
            .action-button {
              display: flex;
              align-items: center;
              gap: 0.25rem;
              padding: 0.5rem 0.75rem;
              border-radius: 6px;
              border: 1px solid #e2e8f0;
              background: white;
              color: #374151;
              text-decoration: none;
              transition: all 0.2s ease;
              font-size: 0.8rem;
              font-weight: 500;
              cursor: pointer;
            }
            
            .action-button:hover:not(:disabled) {
              border-color: #3b82f6;
              color: #3b82f6;
              background: #f8fafc;
              text-decoration: none;
            }
            
            .action-button:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            
            .action-button.danger {
              color: #dc2626;
              border-color: #fecaca;
            }
            
            .action-button.danger:hover:not(:disabled) {
              background: #fef2f2;
              border-color: #dc2626;
              color: #dc2626;
            }
          `}</style>

          {/* Header with balance status */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h6 className="fw-medium mb-1">{selectedRow?.description}</h6>
              <div className="small text-muted">
                {moment(selectedRow?.date).format('D MMMM YYYY')} • 
                Skapat {moment(selectedRow?.created_at).format('D MMM YYYY HH:mm')}
              </div>
            </div>
            <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
              {isBalanced ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
              {isBalanced ? 'Balanserad' : 'Obalanserad'}
            </div>
          </div>

          {/* Basic Information Grid - Updated with creator info */}
          <div className="verification-section">
            <div className="d-flex align-items-center mb-3">
              <Calendar className="section-icon me-2" />
              <h6 className="mb-0 fw-medium">Grundinformation</h6>
            </div>
            <div className="info-grid">
              <div className="info-card">
                <div className="info-label">Verifikatsdatum</div>
                <div className="info-value">{moment(selectedRow?.date).format('D MMMM YYYY')}</div>
              </div>
              <div className="info-card">
                <div className="info-label">Serie & Nummer</div>
                <div className="info-value">{selectedRow?.series}{selectedRow?.number}</div>
              </div>
              <div className="info-card">
                <div className="info-label">Skapat</div>
                <div className="info-value">{moment(selectedRow?.created_at).format('D MMM YYYY HH:mm')}</div>
              </div>
              {selectedRow?.reference && (
                <div className="info-card">
                  <div className="info-label">Referens</div>
                  <div className="info-value">{selectedRow.reference}</div>
                </div>
              )}
            </div>

            {/* Creator Information Card */}
            {selectedRow?.created_by_info && (
              <div className="mt-3">
                <div className="info-label mb-2">Skapad av</div>
                <div className="creator-info-card">
                  <div className="creator-avatar">
                    {selectedRow.created_by_info.full_name ? 
                      selectedRow.created_by_info.full_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                      selectedRow.created_by_info.first_name ? 
                        selectedRow.created_by_info.first_name[0].toUpperCase() :
                        <User size={16} />
                    }
                  </div>
                  <div className="creator-details">
                    <div className="creator-name">
                      {formatCreatorInfo(selectedRow.created_by_info)}
                    </div>
                    {selectedRow.created_by_info.email && (
                      <div className="creator-email">
                        {selectedRow.created_by_info.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Notes section if available */}
            {selectedRow?.notes && (
              <div className="mt-3">
                <div className="info-label mb-2">Anteckningar</div>
                <div className="info-card">
                  <div className="info-value" style={{ whiteSpace: 'pre-wrap' }}>
                    {selectedRow.notes}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Transactions section */}
          <div className="verification-section">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center">
                <Calculator className="section-icon me-2" />
                <h6 className="mb-0 fw-medium">Transaktioner</h6>
              </div>
              <div className="small text-muted">
                {selectedRow?.transactions?.length || 0} st
              </div>
            </div>

            <div className="compact-table">
              <div className="compact-table-header d-flex">
                <div style={{ flex: '3' }}>Konto</div>
                <div style={{ flex: '1', textAlign: 'right' }}>Debet</div>
                <div style={{ flex: '1', textAlign: 'right' }}>Kredit</div>
              </div>
              
              {selectedRow?.transactions?.map((transaction, index) => (
                <div key={index} className="compact-table-row d-flex align-items-center">
                  <div style={{ flex: '3' }}>
                    <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
                      {transaction.account_number} - {transaction.account_name}
                    </div>
                  </div>
                  <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                    {transaction.transaction_type === 'debit' ? (
                      <span className="text-success fw-medium">{formatCurrency(transaction.amount)}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </div>
                  <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
                    {transaction.transaction_type === 'credit' ? (
                      <span className="text-primary fw-medium">{formatCurrency(transaction.amount)}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </div>
                </div>
              ))}
              
              <div className="compact-table-footer d-flex">
                <div style={{ flex: '3' }}>Summa</div>
                <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
                  {formatCurrency(selectedRow?.debitSum || 0)}
                </div>
                <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
                  {formatCurrency(selectedRow?.creditSum || 0)}
                </div>
              </div>
            </div>

            {!isBalanced && (
              <div className="alert alert-warning mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
                <AlertTriangle size={14} className="me-2" />
                <div>
                  <strong>Varning:</strong> Transaktioner är inte balanserade.
                </div>
              </div>
            )}
          </div>

          {/* Invoice Section */}
          {selectedRow?.invoice && (
            <InvoiceSection invoice={selectedRow.invoice} companyId={companyId} />
          )}

          {/* Attachments */}
          {loadingAttachments ? (
            <div className="verification-section">
              <div className="text-center py-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Laddar...</span>
                </div>
                <p className="mt-2 small">Hämtar bilagor...</p>
              </div>
            </div>
          ) : attachmentError ? (
            <div className="verification-section">
              <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.8rem' }}>{attachmentError}</div>
            </div>
          ) : attachments.length > 0 || permissions.canEdit ? (
            <div className="verification-section">
              <div className="d-flex align-items-center mb-3">
                <Paperclip className="section-icon me-2" />
                <h6 className="mb-0 fw-medium">
                  Bilagor {attachments.length > 0 && `(${attachments.length})`}
                </h6>
              </div>
              
              {/* Files Display Area */}
              <div className="d-flex flex-wrap gap-2 align-items-start">
                {/* Existing Attachments */}
                {attachments.map((attachment, index) => (
                  <div key={index} className="file-item position-relative">
                    <div 
                      className="file-content" 
                      onMouseEnter={(e) => handleAttachmentHover(attachment, e)}
                      onMouseLeave={handleAttachmentLeave}
                      onClick={() => handleAttachmentClick(attachment)}
                    >
                      <div className="file-icon-container">
                        {attachment.thumbnailUrl && !attachment.error ? (
                          <img
                            src={attachment.thumbnailUrl}
                            alt={attachment.file_name}
                            className="file-thumbnail"
                          />
                        ) : (
                          <File size={24} className="file-icon" />
                        )}
                      </div>
                      <div className="file-info">
                        <div className="file-name" title={attachment.file_name}>
                          {attachment.file_name.length > 12 
                            ? attachment.file_name.substring(0, 12) + '...' 
                            : attachment.file_name}
                        </div>
                        <div className="file-size">
                          {formatFileSize(attachment.file_size)}
                        </div>
                      </div>
                    </div>
                    {/* Delete button - Only show if user can edit */}
                    {permissions.canEdit && (
                      <button
                        className="btn btn-sm btn-outline-danger position-absolute"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAttachment(attachment.attachment_id, attachment.file_name);
                        }}
                        title="Ta bort bilaga"
                        style={{
                          top: '-8px',
                          right: '-8px',
                          fontSize: '10px',
                          padding: '2px 4px',
                          lineHeight: 1,
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}

                {/* Upload Area - Only show if user can edit */}
                {permissions.canEdit && (
                  <div className="file-item file-upload-item">
                    <input
                      type="file"
                      id="verification-file-upload"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls,.txt"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                      disabled={uploadingFiles}
                    />
                    <label htmlFor="verification-file-upload" className="file-upload-label">
                      <div className="file-icon-container">
                        {uploadingFiles ? (
                          <Spinner animation="border" size="sm" className="upload-spinner" />
                        ) : (
                          <Plus size={24} className="upload-icon" />
                        )}
                      </div>
                      <div className="file-info">
                        <div className="file-name">
                          {uploadingFiles ? 'Laddar...' : 'Ladda upp'}
                        </div>
                        <div className="file-size">
                          Max 10MB
                        </div>
                      </div>
                    </label>
                  </div>
                )}
              </div>

              {/* Help text */}
              {permissions.canEdit && (
                <div className="small text-muted mt-2">
                  Stödda filtyper: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, TXT
                </div>
              )}
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className="border-0 pt-2">
          <div className="d-flex gap-2 w-100">
            <button
              className={`action-button ${permissions.canEdit ? '' : 'disabled'}`}
              onClick={handleEdit}
              disabled={!permissions.canEdit}
            >
              <Edit3 size={14} />
              Redigera
            </button>

            <button
              className="action-button"
              onClick={handleCopy}
            >
              <Copy size={14} />
              Kopiera
            </button>

            <button
              className={`action-button ${permissions.canEdit ? '' : 'disabled'}`}
              onClick={() => onUploadAttachment(selectedRow.verification_id)}
              disabled={!permissions.canEdit}
            >
              <Upload size={14} />
              Ladda upp
            </button>

            <button
              className={`action-button danger ${permissions.canDelete ? '' : 'disabled'}`}
              onClick={handleDelete}
              disabled={!permissions.canDelete}
            >
              <Trash2 size={14} />
              Radera
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {permissions.canDelete && selectedRow && (
        <DeleteVerificationModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          verificationData={selectedRow}
          companyId={companyId}
          onVerificationDeleted={onVerificationUpdated}
        />
      )}

      {/* Hover Preview */}
      {hoveredAttachment && (
        <div 
          className="attachment-hover-preview"
          style={{
            left: `${previewPosition.x}px`,
            top: `${previewPosition.y}px`
          }}
        >
          <div className="preview-header">
            <Search size={14} className="magnifying-icon" />
            {hoveredAttachment.file_name}
          </div>
          
          <div className="preview-content">
            {hoveredAttachment.file_type.startsWith('image/') ? (
              loadingPreview ? (
                <div className="preview-placeholder">
                  <div className="spinner-border spinner-border-sm mb-2" role="status">
                    <span className="visually-hidden">Laddar...</span>
                  </div>
                  <div>Laddar bild...</div>
                </div>
              ) : !previewBlobUrl ? (
                <div className="preview-placeholder">
                  <Image size={32} className="mb-2" />
                  <div>Kunde inte ladda bild</div>
                </div>
              ) : (
                <img
                  src={previewBlobUrl}
                  alt={hoveredAttachment.file_name}
                  className="preview-image"
                  onLoad={(e) => {
                    console.log('Image loaded successfully from blob:', e.target.src);
                  }}
                  onError={(e) => {
                    console.error('Image failed to load from blob:', e.target.src);
                  }}
                />
              )
            ) : hoveredAttachment.file_type === 'application/pdf' ? (
              loadingPreview ? (
                <div className="preview-placeholder">
                  <div className="spinner-border spinner-border-sm mb-2" role="status">
                    <span className="visually-hidden">Laddar...</span>
                  </div>
                  <div>Laddar PDF...</div>
                </div>
              ) : !previewBlobUrl ? (
                <div className="preview-placeholder">
                  <FileText size={32} className="mb-2" />
                  <div>Kunde inte ladda PDF</div>
                  <div className="small mt-1">Klicka för att ladda ner</div>
                </div>
              ) : (
                <div className="preview-pdf-container">
                  <iframe
                    src={`${previewBlobUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&view=FitH`}
                    title={hoveredAttachment.file_name}
                    className="preview-pdf-iframe"
                    onLoad={() => {
                      console.log('PDF loaded successfully in iframe');
                    }}
                    onError={() => {
                      console.error('PDF failed to load in iframe');
                    }}
                  />
                </div>
              )
            ) : hoveredAttachment.file_type.startsWith('text/') ? (
              loadingPreview ? (
                <div className="preview-placeholder">
                  <div className="spinner-border spinner-border-sm mb-2" role="status">
                    <span className="visually-hidden">Laddar...</span>
                  </div>
                  <div>Laddar textfil...</div>
                </div>
              ) : !textContent ? (
                <div className="preview-placeholder">
                  <FileText size={32} className="mb-2" />
                  <div>Kunde inte ladda textfil</div>
                  <div className="small mt-1">Klicka för att ladda ner</div>
                </div>
              ) : (
                <div className="preview-text-container">
                  <pre className="preview-text-content">
                    {textContent.substring(0, 500)}{textContent.length > 500 ? '...' : ''}
                  </pre>
                </div>
              )
            ) : hoveredAttachment.file_type.includes('word') || hoveredAttachment.file_name.match(/\.(doc|docx)$/i) ? (
              <div className="preview-placeholder">
                <FileText size={32} className="mb-2" />
                <div>Word-dokument</div>
                <div className="small mt-1">Klicka för att ladda ner</div>
              </div>
            ) : hoveredAttachment.file_type.includes('excel') || hoveredAttachment.file_name.match(/\.(xls|xlsx)$/i) ? (
              <div className="preview-placeholder">
                <FileText size={32} className="mb-2" />
                <div>Excel-dokument</div>
                <div className="small mt-1">Klicka för att ladda ner</div>
              </div>
            ) : (
              <div className="preview-placeholder">
                <File size={32} className="mb-2" />
                <div>Dokument</div>
                <div className="small mt-1">Klicka för att ladda ner</div>
              </div>
            )}
          </div>
          
          <div className="preview-info">
            <span>{formatFileSize(hoveredAttachment.file_size)}</span>
            <span>Klicka för att ladda ned</span>
          </div>
        </div>
      )}
    </>
  );
};

export default VerificationDetailsModal;




// import React, { useState, useEffect, useRef, useMemo } from 'react';
// import { Modal, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
// import moment from 'moment';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faEdit, faCopy, faTrash, faUpload, faFileInvoiceDollar, faSpinner, faLock } from '@fortawesome/free-solid-svg-icons';
// import {
//   Calendar,
//   Clock,
//   FileText,
//   DollarSign,
//   Calculator,
//   Paperclip,
//   Eye,
//   Edit3,
//   Copy,
//   Trash2,
//   Upload,
//   AlertTriangle,
//   CheckCircle,
//   Image,
//   File
// } from 'lucide-react';
// import { useParams, useLocation, useHistory } from 'react-router-dom';
// import DeleteVerificationModal from './DeleteVerificationModal';
// import axios from '../../../../services/AxiosInstance';
// import { getApiUrl } from '../../../../utils/ApiUtils';
// import swal from 'sweetalert';

// const getVerificationPermissions = (verificationData) => {
//   const permissions = {
//     canDelete: true,
//     canEdit: true,
//     reason: null
//   };

//   if (!verificationData || !verificationData.references) {
//     return permissions;
//   }

//   // Check if this is an annulment verification
//   if (verificationData.isAnnulmentVerification) {
//     permissions.canDelete = false;
//     permissions.canEdit = false;
//     permissions.reason = 'Detta är ett annuleringsverifikat och kan inte ändras.';
//     return permissions;
//   }

//   // Check if this verification has been annulled
//   if (verificationData.hasBeenAnnulled) {
//     permissions.canDelete = false;
//     permissions.canEdit = false;
//     permissions.reason = 'Detta verifikat har redan blivit annulerat.';
//     return permissions;
//   }

//   // Check source references (what this verification refers to)
//   for (const ref of verificationData.references?.asSource || []) {
//     switch (ref.reference_type) {
//       case 'customer_invoice_payment':
//         permissions.canDelete = false;
//         permissions.canEdit = false;
//         permissions.reason = 'Detta verifikat är kopplat till en fakturabetalning och kan inte ändras här. Gå till fakturavyn för att hantera betalningen.';
//         return permissions;

//       case 'payment_reversal':
//         permissions.canDelete = false;
//         permissions.canEdit = false;
//         permissions.reason = 'Detta verifikat är kopplat till en betalningsannulering och kan inte ändras.';
//         return permissions;
//     }
//   }

//   // Check target references (what refers to this verification)
//   for (const ref of verificationData.references?.asTarget || []) {
//     if (ref.reference_type === 'verification_reversal') {
//       permissions.canDelete = false;
//       permissions.canEdit = false;
//       permissions.reason = 'Detta verifikat har redan annullerats.';
//       return permissions;
//     }
//   }

//   // Check if this is an invoice related verification
//   if (verificationData.invoice) {
//     permissions.canDelete = false;
//     permissions.canEdit = false;
//     permissions.reason = 'Detta verifikat är kopplat till en faktura och kan inte ändras här. Gå till fakturavyn för att hantera det.';
//     return permissions;
//   }

//   return permissions;
// };

// const InvoiceSection = ({ invoice, companyId }) => {
//   const [pdfUrl, setPdfUrl] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     let objectUrl;

//     const fetchPdf = async () => {
//       try {
//         const response = await axios.get(
//           getApiUrl(`${companyId}/invoices/${invoice.invoice_id}/pdf`),
//           {
//             responseType: 'blob',
//             withCredentials: true,
//           }
//         );

//         objectUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
//         setPdfUrl(objectUrl);
//       } catch (error) {
//         console.error('Error fetching PDF:', error);
//         setError('Det gick inte att ladda fakturan. Försök igen senare.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (invoice?.invoice_id) {
//       fetchPdf();
//     }

//     return () => {
//       if (objectUrl) {
//         URL.revokeObjectURL(objectUrl);
//       }
//     };
//   }, [invoice?.invoice_id, companyId]);

//   if (!invoice?.invoice_id) return null;

//   return (
//     <div className="verification-section">
//       <div className="d-flex align-items-center mb-3">
//         <FileText className="section-icon me-2" />
//         <h6 className="mb-0 fw-medium">Faktura #{invoice.invoice_number}</h6>
//       </div>
//       {loading ? (
//         <div className="text-center py-3">
//           <div className="spinner-border spinner-border-sm" role="status">
//             <span className="visually-hidden">Laddar...</span>
//           </div>
//           <p className="mt-2 small">Laddar faktura...</p>
//         </div>
//       ) : error ? (
//         <div className="text-center py-3">
//           <div className="alert alert-warning">
//             <p className="mb-3 small">{error}</p>
//             <button
//               className="action-button"
//               onClick={() => window.open(`/${companyId}/view-invoice/${invoice.invoice_id}`, '_blank')}
//             >
//               <FileText size={14} />
//               Öppna faktura i ny flik
//             </button>
//           </div>
//         </div>
//       ) : (
//         <div className="border rounded overflow-hidden">
//           <object
//             data={pdfUrl}
//             type="application/pdf"
//             style={{
//               width: '100%',
//               height: '300px',
//               border: 'none'
//             }}
//           >
//             <div className="text-center py-3">
//               <FileText size={32} className="text-muted mb-2" />
//               <p className="small">PDF kunde inte visas</p>
//             </div>
//           </object>
//         </div>
//       )}
//     </div>
//   );
// };

// const VerificationDetailsModal = ({
//   showVerificationDetails,
//   setShowVerificationDetails,
//   selectedRow,
//   onVerificationUpdated,
//   onUploadAttachment,
// }) => {
//   const { companyId } = useParams();
//   const location = useLocation();
//   const history = useHistory();

//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [attachments, setAttachments] = useState([]);
//   const [loadingAttachments, setLoadingAttachments] = useState(false);
//   const [attachmentError, setAttachmentError] = useState(null);

//   console.log('Selected Row:', selectedRow);

//   // Calculate permissions based on verification references
//   const permissions = useMemo(() => {
//     return getVerificationPermissions(selectedRow);
//   }, [selectedRow]);

//   // Reset state when modal opens or closes
//   useEffect(() => {
//     if (!showVerificationDetails) {
//       setAttachments([]);
//       setAttachmentError(null);
//       setLoadingAttachments(false);
//     }
//   }, [showVerificationDetails]);

//   // Fetch attachments when verification is selected
//   useEffect(() => {
//     if (selectedRow?.verification_id && showVerificationDetails) {
//       fetchAttachments();
//     }
//   }, [selectedRow, showVerificationDetails]);

//   // Fetch attachments
//   const fetchAttachments = async () => {
//     if (!selectedRow?.verification_id) return;

//     setLoadingAttachments(true);
//     setAttachmentError(null);

//     try {
//       const response = await axios.get(
//         getApiUrl(`${companyId}/accounting/verifications/${selectedRow.verification_id}/getattachments`),
//         { withCredentials: true }
//       );
//       const attachmentsData = response.data.attachments || [];

//       const attachmentsWithThumbnails = await Promise.all(
//         attachmentsData.map(async (attachment) => {
//           try {
//             const thumbnailResponse = await axios.get(
//               getApiUrl(`${companyId}/accounting/verifications/thumbnail/${attachment.attachment_id}`),
//               {
//                 responseType: 'blob',
//                 withCredentials: true,
//               }
//             );
//             const thumbnailUrl = URL.createObjectURL(thumbnailResponse.data);
//             return { ...attachment, thumbnailUrl };
//           } catch (error) {
//             console.error('Error fetching attachment thumbnail:', error);
//             return { ...attachment, thumbnailUrl: null, error: true };
//           }
//         })
//       );

//       setAttachments(attachmentsWithThumbnails);
//     } catch (error) {
//       console.error('Error fetching attachments:', error);
//       setAttachmentError('Det gick inte att hämta bilagorna. Försök igen senare.');
//     } finally {
//       setLoadingAttachments(false);
//     }
//   };

//   // Cleanup object URLs
//   useEffect(() => {
//     return () => {
//       attachments.forEach((attachment) => {
//         if (attachment.thumbnailUrl) {
//           URL.revokeObjectURL(attachment.thumbnailUrl);
//         }
//       });
//     };
//   }, [attachments]);

//   const handleDelete = () => {
//     if (!permissions.canDelete) {
//       swal("Kan inte radera", permissions.reason, "warning");
//       return;
//     }
//     setShowDeleteModal(true);
//     setShowVerificationDetails(false);
//   };

//   const handleEdit = () => {
//     if (!permissions.canEdit) {
//       swal("Kan inte redigera", permissions.reason, "warning");
//       return;
//     }
//     setShowVerificationDetails(false);
//     openEditVerificationModal(selectedRow);
//   };

//   const handleCopy = () => {
//     setShowVerificationDetails(false);
//     openCreateVerificationModal(selectedRow);
//   };

//   const openCreateVerificationModal = (verificationData) => {
//     history.push({
//       pathname: `/${companyId}/copy-verification`,
//       state: { verificationData, from: location.pathname + '?updated=true' },
//     });
//   };

//   const openEditVerificationModal = (verificationData) => {
//     history.push({
//       pathname: `/${companyId}/edit-verification/${verificationData.verification_id}`,
//       state: { verificationData, from: location.pathname + '?updated=true' },
//     });
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
//   };

//   const handleCloseModal = () => {
//     setShowVerificationDetails(false);
//     if (onVerificationUpdated) {
//       onVerificationUpdated();
//     }
//   };

//   const getFileIcon = (fileType) => {
//     if (fileType.startsWith('image/')) return <Image size={16} className="text-success" />;
//     if (fileType.includes('pdf')) return <FileText size={16} className="text-danger" />;
//     return <File size={16} className="text-primary" />;
//   };

//   const formatFileSize = (bytes) => {
//     if (bytes === 0) return '0 Bytes';
//     const k = 1024;
//     const sizes = ['Bytes', 'KB', 'MB', 'GB'];
//     const i = Math.floor(Math.log(bytes) / Math.log(k));
//     return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
//   };

//   // Calculate if verification is balanced
//   const isBalanced = Math.abs((selectedRow?.debitSum || 0) - (selectedRow?.creditSum || 0)) <= 0.01;

//   return (
//     <>
//       <Modal
//         show={showVerificationDetails}
//         onHide={handleCloseModal}
//         size="lg"
//         centered
//       >
//         <Modal.Header closeButton className="border-0 pb-2">
//           <Modal.Title className="h5 fw-medium">
//             <div className="d-flex align-items-center">
//               <Eye className="me-2" size={20} style={{ color: '#3b82f6' }} />
//               <div>
//                 <div className="fw-medium">
//                   Verifikat {selectedRow?.series}{selectedRow?.number}
//                   {(!permissions.canEdit || !permissions.canDelete) && (
//                     <OverlayTrigger
//                       placement="right"
//                       overlay={<Tooltip>{permissions.reason}</Tooltip>}
//                     >
//                       <FontAwesomeIcon
//                         icon={faLock}
//                         className="ms-2 text-muted"
//                         size="sm"
//                       />
//                     </OverlayTrigger>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </Modal.Title>
//         </Modal.Header>

//         <Modal.Body className="px-4 py-3">
//           <style>{`
//             .verification-section {
//               background: #f8fafc;
//               border-radius: 8px;
//               padding: 1rem;
//               margin-bottom: 1rem;
//               border: 1px solid #e2e8f0;
//             }
            
//             .section-icon {
//               width: 16px;
//               height: 16px;
//               color: #64748b;
//             }
            
//             .balance-indicator {
//               display: inline-flex;
//               align-items: center;
//               gap: 0.25rem;
//               padding: 0.25rem 0.75rem;
//               border-radius: 6px;
//               font-weight: 500;
//               font-size: 0.75rem;
//             }
            
//             .balance-good {
//               background: #dcfce7;
//               color: #166534;
//               border: 1px solid #bbf7d0;
//             }
            
//             .balance-bad {
//               background: #fef2f2;
//               color: #dc2626;
//               border: 1px solid #fecaca;
//             }
            
//             .compact-table {
//               background: white;
//               border-radius: 6px;
//               overflow: hidden;
//               border: 1px solid #e2e8f0;
//             }
            
//             .compact-table-header {
//               background: #f8fafc;
//               border-bottom: 1px solid #e2e8f0;
//               padding: 0.5rem 0.75rem;
//               font-size: 0.75rem;
//               font-weight: 600;
//               text-transform: uppercase;
//               letter-spacing: 0.025em;
//               color: #64748b;
//             }
            
//             .compact-table-row {
//               padding: 0.5rem 0.75rem;
//               border-bottom: 1px solid #f1f5f9;
//               font-size: 0.875rem;
//               transition: background-color 0.15s ease;
//             }
            
//             .compact-table-row:hover {
//               background: #f8fafc;
//             }
            
//             .compact-table-row:last-child {
//               border-bottom: none;
//             }
            
//             .compact-table-footer {
//               background: #f1f5f9;
//               padding: 0.5rem 0.75rem;
//               font-weight: 600;
//               font-size: 0.875rem;
//               border-top: 1px solid #e2e8f0;
//             }
            
//             .amount-cell {
//               font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
//               font-size: 0.8rem;
//             }
            
//             .attachment-grid {
//               display: grid;
//               grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
//               gap: 0.75rem;
//             }
            
//             .attachment-card {
//               background: white;
//               border: 1px solid #e2e8f0;
//               border-radius: 6px;
//               padding: 0.75rem;
//               transition: all 0.2s ease;
//             }
            
//             .attachment-card:hover {
//               border-color: #3b82f6;
//               box-shadow: 0 2px 8px rgba(59, 130, 246, 0.15);
//               transform: translateY(-1px);
//             }
            
//             .info-grid {
//               display: grid;
//               grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
//               gap: 1rem;
//             }
            
//             .info-card {
//               background: white;
//               border: 1px solid #e2e8f0;
//               border-radius: 6px;
//               padding: 0.75rem;
//             }
            
//             .info-label {
//               font-size: 0.7rem;
//               color: #64748b;
//               text-transform: uppercase;
//               letter-spacing: 0.025em;
//               margin-bottom: 0.25rem;
//               font-weight: 600;
//             }
            
//             .info-value {
//               font-size: 0.875rem;
//               font-weight: 500;
//               color: #1e293b;
//             }
            
//             .action-button {
//               display: flex;
//               align-items: center;
//               gap: 0.25rem;
//               padding: 0.5rem 0.75rem;
//               border-radius: 6px;
//               border: 1px solid #e2e8f0;
//               background: white;
//               color: #374151;
//               text-decoration: none;
//               transition: all 0.2s ease;
//               font-size: 0.8rem;
//               font-weight: 500;
//               cursor: pointer;
//             }
            
//             .action-button:hover:not(:disabled) {
//               border-color: #3b82f6;
//               color: #3b82f6;
//               background: #f8fafc;
//               text-decoration: none;
//             }
            
//             .action-button:disabled {
//               opacity: 0.5;
//               cursor: not-allowed;
//             }
            
//             .action-button.danger {
//               color: #dc2626;
//               border-color: #fecaca;
//             }
            
//             .action-button.danger:hover:not(:disabled) {
//               background: #fef2f2;
//               border-color: #dc2626;
//               color: #dc2626;
//             }
//           `}</style>

//           {/* Header with balance status */}
//           <div className="d-flex justify-content-between align-items-start mb-3">
//             <div>
//               <h6 className="fw-medium mb-1">{selectedRow?.description}</h6>
//               <div className="small text-muted">
//                 {moment(selectedRow?.date).format('D MMMM YYYY')} • 
//                 Skapat {moment(selectedRow?.created_at).format('D MMM YYYY HH:mm')}
//               </div>
//             </div>
//             <div className={`balance-indicator ${isBalanced ? 'balance-good' : 'balance-bad'}`}>
//               {isBalanced ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
//               {isBalanced ? 'Balanserad' : 'Obalanserad'}
//             </div>
//           </div>

//           {/* Basic Information Grid */}
//           <div className="verification-section">
//             <div className="d-flex align-items-center mb-3">
//               <Calendar className="section-icon me-2" />
//               <h6 className="mb-0 fw-medium">Grundinformation</h6>
//             </div>
//             <div className="info-grid">
//               <div className="info-card">
//                 <div className="info-label">Verifikatsdatum</div>
//                 <div className="info-value">{moment(selectedRow?.date).format('D MMMM YYYY')}</div>
//               </div>
//               <div className="info-card">
//                 <div className="info-label">Serie & Nummer</div>
//                 <div className="info-value">{selectedRow?.series}{selectedRow?.number}</div>
//               </div>
//               <div className="info-card">
//                 <div className="info-label">Skapat</div>
//                 <div className="info-value">{moment(selectedRow?.created_at).format('D MMM YYYY HH:mm')}</div>
//               </div>
//               {/*<div className="info-card">
//                 <div className="info-label">Status</div>
//                 <div className="info-value">
//                   <span className={`badge ${isBalanced ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '0.7rem' }}>
//                     {isBalanced ? 'Balanserad' : 'Obalanserad'}
//                   </span>
//                 </div>
//               </div>*/}
//             </div>
//           </div>

//           {/* Transactions */}
//           <div className="verification-section">
//             <div className="d-flex align-items-center justify-content-between mb-3">
//               <div className="d-flex align-items-center">
//                 <Calculator className="section-icon me-2" />
//                 <h6 className="mb-0 fw-medium">Transaktioner</h6>
//               </div>
//               <div className="small text-muted">
//                 {selectedRow?.transactions?.length || 0} st
//               </div>
//             </div>

//             <div className="compact-table">
//               <div className="compact-table-header d-flex">
//                 <div style={{ flex: '3' }}>Konto</div>
//                 <div style={{ flex: '1', textAlign: 'right' }}>Debet</div>
//                 <div style={{ flex: '1', textAlign: 'right' }}>Kredit</div>
//               </div>
              
//               {selectedRow?.transactions?.map((transaction, index) => (
//                 <div key={index} className="compact-table-row d-flex align-items-center">
//                   <div style={{ flex: '3' }}>
//                     <div className="fw-medium" style={{ fontSize: '0.875rem' }}>
//                       {transaction.account_number} - {transaction.account_name}
//                     </div>
//                   </div>
//                   <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
//                     {transaction.transaction_type === 'debit' ? (
//                       <span className="text-success fw-medium">{formatCurrency(transaction.amount)}</span>
//                     ) : (
//                       <span className="text-muted">—</span>
//                     )}
//                   </div>
//                   <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell">
//                     {transaction.transaction_type === 'credit' ? (
//                       <span className="text-primary fw-medium">{formatCurrency(transaction.amount)}</span>
//                     ) : (
//                       <span className="text-muted">—</span>
//                     )}
//                   </div>
//                 </div>
//               ))}
              
//               <div className="compact-table-footer d-flex">
//                 <div style={{ flex: '3' }}>Summa</div>
//                 <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-success">
//                   {formatCurrency(selectedRow?.debitSum || 0)}
//                 </div>
//                 <div style={{ flex: '1', textAlign: 'right' }} className="amount-cell text-primary">
//                   {formatCurrency(selectedRow?.creditSum || 0)}
//                 </div>
//               </div>
//             </div>

//             {!isBalanced && (
//               <div className="alert alert-warning mt-2 py-2 px-3 d-flex align-items-center" style={{ fontSize: '0.8rem' }}>
//                 <AlertTriangle size={14} className="me-2" />
//                 <div>
//                   <strong>Varning:</strong> Transaktioner är inte balanserade.
//                 </div>
//               </div>
//             )}
//           </div>

//           {/* Invoice Section */}
//           {selectedRow?.invoice && (
//             <InvoiceSection invoice={selectedRow.invoice} companyId={companyId} />
//           )}

//           {/* Attachments */}
//           {loadingAttachments ? (
//             <div className="verification-section">
//               <div className="text-center py-2">
//                 <div className="spinner-border spinner-border-sm" role="status">
//                   <span className="visually-hidden">Laddar...</span>
//                 </div>
//                 <p className="mt-2 small">Hämtar bilagor...</p>
//               </div>
//             </div>
//           ) : attachmentError ? (
//             <div className="verification-section">
//               <div className="alert alert-danger py-2 px-3" style={{ fontSize: '0.8rem' }}>{attachmentError}</div>
//             </div>
//           ) : attachments.length > 0 ? (
//             <div className="verification-section">
//               <div className="d-flex align-items-center mb-3">
//                 <Paperclip className="section-icon me-2" />
//                 <h6 className="mb-0 fw-medium">
//                   Bilagor ({attachments.length})
//                 </h6>
//               </div>
//               <div className="attachment-grid">
//                 {attachments.map((attachment, index) => (
//                   <div key={index} className="attachment-card">
//                     <div className="d-flex align-items-start gap-2">
//                       <div className="flex-shrink-0">
//                         {attachment.thumbnailUrl && !attachment.error ? (
//                           <img
//                             src={attachment.thumbnailUrl}
//                             alt={attachment.file_name}
//                             className="rounded"
//                             style={{ width: '32px', height: '32px', objectFit: 'cover' }}
//                           />
//                         ) : (
//                           <div className="d-flex align-items-center justify-content-center bg-light rounded"
//                             style={{ width: '32px', height: '32px' }}>
//                             {getFileIcon(attachment.file_type)}
//                           </div>
//                         )}
//                       </div>
//                       <div className="flex-grow-1 min-w-0">
//                         <div className="fw-medium text-truncate" style={{ fontSize: '0.8rem' }}>
//                           {attachment.file_name}
//                         </div>
//                         <div className="small text-muted" style={{ fontSize: '0.7rem' }}>
//                           {formatFileSize(attachment.file_size)}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             </div>
//           ) : null}
//         </Modal.Body>

//         <Modal.Footer className="border-0 pt-2">
//           <div className="d-flex gap-2 w-100">
//             <button
//               className={`action-button ${permissions.canEdit ? '' : 'disabled'}`}
//               onClick={handleEdit}
//               disabled={!permissions.canEdit}
//             >
//               <Edit3 size={14} />
//               Redigera
//             </button>

//             <button
//               className="action-button"
//               onClick={handleCopy}
//             >
//               <Copy size={14} />
//               Kopiera
//             </button>

//             <button
//               className={`action-button ${permissions.canEdit ? '' : 'disabled'}`}
//               onClick={() => onUploadAttachment(selectedRow.verification_id)}
//               disabled={!permissions.canEdit}
//             >
//               <Upload size={14} />
//               Ladda upp
//             </button>

//             <button
//               className={`action-button danger ${permissions.canDelete ? '' : 'disabled'}`}
//               onClick={handleDelete}
//               disabled={!permissions.canDelete}
//             >
//               <Trash2 size={14} />
//               Radera
//             </button>
//           </div>
//         </Modal.Footer>
//       </Modal>

//       {permissions.canDelete && selectedRow && (
//         <DeleteVerificationModal
//           show={showDeleteModal}
//           handleClose={() => setShowDeleteModal(false)}
//           verificationData={selectedRow}
//           companyId={companyId}
//           onVerificationDeleted={onVerificationUpdated}
//         />
//       )}
//     </>
//   );
// };

// export default VerificationDetailsModal;
